import { useCallback, useEffect, useRef, useState } from "react";
import type { ParagrafGruppe } from "../hooks/useVertragstext";
import { useEntwurfMelden } from "../hooks/useEntwuerfe";

/**
 * Kopfzeile eines Paragrafen — und zugleich sein Klapp-Auslöser.
 *
 * Zugeklappt ist der Titel ein Knopf, aufgeklappt ein Eingabefeld. So bleibt
 * die ganze Zeile anklickbar, ohne dass ein Bedienelement in einem anderen
 * steckt: Klapp-Knopf und Titel sind Geschwister, nie ineinander.
 *
 * Vierzehn Paragrafen mit achtundsiebzig Absätzen ergeben aufgeklappt eine
 * Seite, auf der man scrollt statt arbeitet. Deshalb ist zu der Normalfall —
 * und der geschlossene Kopf sagt trotzdem, was drinsteckt.
 *
 * Bis zum 28.09.2026 standen hier außerdem „umbrechen" (darf der Abschnitt im
 * PDF über die Seite laufen) und „→ in die AGB / → in den Vertrag". Benjamin:
 * „echt verwirrend". Das erste ist Satztechnik und bleibt, wie es gespeichert
 * ist; das zweite war das Werkzeug für den einmaligen Umbau in Urkunde und AGB.
 * Die AGB haben seitdem ihre eigene Seite.
 */
export function ParagrafKopf({
  gruppe, offen, onToggle, schreibbar, onTitel, onHoch, onRunter,
}: {
  gruppe: ParagrafGruppe;
  offen: boolean;
  onToggle: () => void;
  schreibbar: boolean;
  onTitel: (titel: string) => void;
  onHoch?: () => void;
  onRunter?: () => void;
}) {
  const [titel, setTitel] = useState(gruppe.titel);
  useEffect(() => setTitel(gruppe.titel), [gruppe.titel]);
  // Verlassen des Feldes und „Speichern" kommen oft direkt nacheinander —
  // dieselbe Überschrift soll nur einmal an den Server gehen.
  const gesendet = useRef<string | null>(null);
  useEffect(() => { gesendet.current = null; }, [gruppe.titel]);
  const sichern = useCallback(() => {
    if (titel === gruppe.titel || gesendet.current === titel) return;
    gesendet.current = titel;
    onTitel(titel);
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [titel, gruppe.titel]);
  useEntwurfMelden(`titel:${gruppe.dokument}-${gruppe.anhang || ""}-${gruppe.paragraf}`,
                   offen && schreibbar && titel !== gruppe.titel, sichern);

  const absaetze = gruppe.klauseln.filter(k => k.art === "absatz").length;
  const bausteine = gruppe.klauseln.filter(k => k.art === "baustein").length;
  const bedingt = gruppe.klauseln.filter(k => k.bedingung).length;

  const zusammenfassung = [
    `${absaetze} ${absaetze === 1 ? "Absatz" : "Absätze"}`,
    bausteine ? `${bausteine} ${bausteine === 1 ? "Baustein" : "Bausteine"}` : "",
    // „bedingt" ist die Eigenschaft, die man von außen nicht sieht und die am
    // ehesten überrascht — deshalb steht sie schon im geschlossenen Kopf.
    bedingt ? `${bedingt}× bedingt` : "",
  ].filter(Boolean).join(" · ");

  return (
    <div className={"vt-par-kopf" + (offen ? " is-offen" : "")}>
      <button
        className="vt-par-klapp" onClick={onToggle} aria-expanded={offen}
        title={offen ? "Zuklappen" : "Aufklappen"}
      >
        <span className="vt-par-chevron" aria-hidden="true">{offen ? "▾" : "▸"}</span>
        <span className="vt-par-nr">
          {gruppe.bezeichnung || gruppe.anhang || "–"}
        </span>
      </button>

      {offen ? (
        <input
          className="vt-par-titel" value={titel} readOnly={!schreibbar}
          placeholder="Überschrift des Abschnitts"
          onChange={e => setTitel(e.target.value)}
          onBlur={sichern}
        />
      ) : (
        <button className="vt-par-titel is-zu" onClick={onToggle}>
          <span className="vt-par-titel-text">{gruppe.titel || "ohne Überschrift"}</span>
          <span className="vt-par-zaehler">{zusammenfassung}</span>
        </button>
      )}

      {schreibbar && (
        <div className="vt-klausel-tools">
          <button className="pf-iconbtn" onClick={onHoch} disabled={!onHoch}
                  title="Paragraf nach oben">↑</button>
          <button className="pf-iconbtn" onClick={onRunter} disabled={!onRunter}
                  title="Paragraf nach unten">↓</button>
        </div>
      )}
    </div>
  );
}
